<template>
  <div class="h-100 p-2">
    <div class="d-flex justify-content-end">
      <v-chart-dropdown
        :download-enabled="true"
        :name="chartName"
      />
    </div>

    <canvas ref="chart" class="chart"></canvas>

    <!-- Invisible chart for downloading -->
    <canvas
      ref="chartDownload"
      class="chart-download"
    ></canvas>
  </div>
</template>

<script>
import {
  Chart,
  registerables,
} from 'chart.js';
import {
  defaultChartTitleConfig,
} from '@/organisation/services/charts';
import { slugify } from '@/common/utils/string';
import ChartDropdown from '@/organisation/components/dashboard/ChartDropdown.vue';
import {
  CHART_COLORS,
  COLOR_SCHEMES,
} from '@/common/utils/constants';
import ChartDataLabels from 'chartjs-plugin-datalabels';


export default {
  name: 'ItemDataHistogram',
  components: {
    'v-chart-dropdown': ChartDropdown,
  },
  props: {
    frequencies: {
      // Array or arrays, like: [[1, 30], [2, 60], [3, 10]]
      type: Array,
    },
    itemTitle: {
      type: String,
    },
    otherResearchPercentage: {
      type: Object,
      required: false,
    },
    swapColors: {
      type: Boolean,
      default() {
        return false;
      },
    },
  },
  data() {
    return {
      answerFrequency: null,
    };
  },
  methods: {
    async getChartConfig(includeLogo, title) {
      const dataSets = [this.dataSetResearch];
      if (this.otherResearchPercentage) {
        dataSets.push(this.dataSetOtherResearch);
      }

      // TODO: use default config function (defaultChartOptions) or Chart.vue;
      const config = {
        type: 'bar',
        data: {
          labels: this.labels,
          datasets: dataSets,
        },
        plugins: [
          ChartDataLabels,
        ],
        options: {
          responsive: !includeLogo,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: dataSets.length > 1,
            },
            title: defaultChartTitleConfig(title),
            tooltip: {
              callbacks: {
                label(context) {
                  return `${context.parsed.y.toFixed(1)}%`;
                },
              },
            },
            datalabels: {
              color: 'white',
              formatter(value) {
                if (value > 3) {
                  return `${value.toFixed(0)}%`;
                }
                return '';
              },
            },
          },
          layout: {
            padding: {
              top: 0,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback(val) {
                  return `${Math.round(val)}%`;
                },
              },
              grid: {
                display: false,
              },
            },
            x: {
              grid: {
                display: false,
              },
              ticks: {
                callback(val) {
                  const value = this.getLabelForValue(val);
                  if (value && value.length > 30) {
                    return `${value.slice(0, 30)}...`;
                  }
                  return value || '-';
                },
              },
            },
          },
        },
      };
      return config;
    },
    async createChart() {
      Chart.register(...registerables);
      // eslint-disable-next-line no-new
      new Chart(
        this.$refs.chart.getContext('2d'),
        await this.getChartConfig(false, null),
      );

      // eslint-disable-next-line no-new
      new Chart(
        this.$refs.chartDownload.getContext('2d'),
        await this.getChartConfig(
          true,
          this.itemTitle,
        ),
      );
    },
  },
  computed: {
    labels() {
      return this.frequencies.map((word) => (word[0]));
    },
    dataSetResearch() {
      let colors;
      if (this.swapColors) {
        colors = CHART_COLORS;
      } else {
        colors = [
          COLOR_SCHEMES.red[2],
        ];
      }
      return {
        label: this.$t('phrase.this_research'),
        data: this.frequencies.map((word) => (word[1])),
        backgroundColor: colors,
      };
    },
    dataSetOtherResearch() {
      return {
        label: this.$t('phrase.other_research'),
        data: this.frequencies.map((word) => (this.otherResearchPercentage[word[0]] || 0)),
        backgroundColor: [
          COLOR_SCHEMES.blue[4],
        ],
        borderColor: [
          `${COLOR_SCHEMES.blue[4]}99`,
        ],
        borderWidth: 3,
      };
    },
    chartName() {
      return `histogram-${slugify(this.itemTitle)}`;
    },
  },
  mounted() {
    this.createChart();
  },
};
</script>
